import getOption, {
  OPTION_ALL_VALUE,
  // OPTION_NO_PARTNER_GROUP_VALUE,
} from 'lib/getOption'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ASSIGNMENT_CAP_MANAGEMENT_DELTA_EQUAL,
  ASSIGNMENT_CAP_MANAGEMENT_DELTA_LIST,
  ASSIGNMENT_CAP_MANAGEMENT_DELTA_UNDER,
} from '../Assignment/assignmentLib'

function secondBlockFilter(item, filters) {
  if (
    filters.delta !== OPTION_ALL_VALUE &&
    (filters.delta === ASSIGNMENT_CAP_MANAGEMENT_DELTA_EQUAL
      ? item.delta !== 0
      : filters.delta === ASSIGNMENT_CAP_MANAGEMENT_DELTA_UNDER
      ? item.delta >= 0
      : item.delta <= 0)
  )
    return false

  if (
    filters.deltaTotal !== OPTION_ALL_VALUE &&
    (filters.deltaTotal === ASSIGNMENT_CAP_MANAGEMENT_DELTA_EQUAL
      ? item.deltaTotal !== 0
      : filters.deltaTotal === ASSIGNMENT_CAP_MANAGEMENT_DELTA_UNDER
      ? item.deltaTotal >= 0
      : item.deltaTotal <= 0)
  )
    return false

  return true
}

function firstBlockFilter(item, filters) {
  if (
    item.automatic < filters.automaticMin ||
    item.automatic > filters.automaticMax
  )
    return false

  if (
    item.teamProposal < filters.proposalMin ||
    item.teamProposal > filters.proposalMax
  )
    return false

  if (item.cap < filters.capMin || item.cap > filters.capMax) return false

  if (item.avgIndex < filters.indexMin || item.avgIndex > filters.indexMax)
    return false

  if (
    [item.partnerId, OPTION_ALL_VALUE].every(
      (partner) => !filters.partners.includes(partner)
    )
  )
    return false

  if (
    [item.partnerGroup ?? '', OPTION_ALL_VALUE].every(
      (partner) => !filters.partnersGroup.includes(partner)
    )
  )
    return false

  return true
}

const useCapManagementFilters = () => {
  const { t } = useTranslation()

  const allOption = useMemo(
    () => getOption(t('common.form.all'), OPTION_ALL_VALUE),
    [t]
  )

  // const noPartnerGroupOption = useMemo(
  //   () =>
  //     getOption(t('common.form.noPartnerGroup'), OPTION_NO_PARTNER_GROUP_VALUE),
  //   [t]
  // )

  const defaultFilters = useMemo(
    () => ({
      indexMin: 0,
      indexMax: 7,
      automaticMin: 0,
      automaticMax: 0,
      proposalMin: 0,
      proposalMax: 0,
      capMin: 0,
      capMax: 0,
      partners: [OPTION_ALL_VALUE],
      partnersGroup: [OPTION_ALL_VALUE],
      delta: OPTION_ALL_VALUE,
      deltaTotal: OPTION_ALL_VALUE,
    }),
    []
  )

  const [filters, setFilters] = useState({
    ...defaultFilters,
    initialized: false,
  })

  const [filtersData, setFiltersData] = useState({
    indexMin: 0,
    indexMax: 7,
    automaticMin: 0,
    automaticMax: 0,
    proposalMin: 0,
    proposalMax: 0,
    capMin: 0,
    capMax: 0,
    partners: [allOption],
    delta: [
      allOption,
      ...ASSIGNMENT_CAP_MANAGEMENT_DELTA_LIST.map((delta) =>
        getOption(t(`assignment.cap-management.delta.${delta}`), delta)
      ),
    ],
    deltaTotal: [
      allOption,
      ...ASSIGNMENT_CAP_MANAGEMENT_DELTA_LIST.map((delta) =>
        getOption(t(`assignment.cap-management.delta.${delta}`), delta)
      ),
    ],
  })

  const resetFilters = useCallback(() => {
    setFilters((s) => ({
      ...s,
      ...defaultFilters,
      indexMin: filtersData.indexMin,
      indexMax: filtersData.indexMax,
      automaticMin: filtersData.automaticMin,
      automaticMax: filtersData.automaticMax,
      proposalMin: filtersData.proposalMin,
      proposalMax: filtersData.proposalMax,
      capMin: filtersData.capMin,
      capMax: filtersData.capMax,
    }))
  }, [
    defaultFilters,
    filtersData.automaticMax,
    filtersData.automaticMin,
    filtersData.capMax,
    filtersData.capMin,
    filtersData.indexMax,
    filtersData.indexMin,
    filtersData.proposalMax,
    filtersData.proposalMin,
  ])

  const initFilters = useCallback(
    async (partners) => {
      const automaticMax = Math.max(
        ...partners.map((partner) => partner.automatic)
      )

      const proposalMax = Math.max(
        ...partners.map((partner) => partner.teamProposal)
      )

      const capMax = Math.max(...partners.map((partner) => partner.cap))

      setFiltersData((s) => ({
        ...s,
        automaticMax,
        proposalMax,
        capMax,
        partners: [
          allOption,
          ...partners.map((partner) =>
            getOption(partner.name, partner.partnerId)
          ),
        ],
        partnersGroup: [
          allOption,
          ...partners
            .map((partner) => getOption(partner.partnerGroup))
            .reduce(
              (acc, curr) =>
                acc.findIndex((f) => f.label === curr.label) > -1
                  ? acc
                  : [...acc, curr],
              []
            )
            .filter((f) => f.label),
          // noPartnerGroupOption,
        ],
      }))

      setFilters({
        ...defaultFilters,
        automaticMax,
        proposalMax,
        capMax,
        initialized: true,
      })
    },
    [allOption, defaultFilters]
  )

  const setAutomaticRange = useCallback(
    (automaticMin, automaticMax) => {
      setFilters((s) => ({ ...s, automaticMin, automaticMax }))
    },
    [setFilters]
  )

  const setProposalRange = useCallback(
    (proposalMin, proposalMax) => {
      setFilters((s) => ({ ...s, proposalMin, proposalMax }))
    },
    [setFilters]
  )

  const setCapRange = useCallback(
    (capMin, capMax) => {
      setFilters((s) => ({ ...s, capMin, capMax }))
    },
    [setFilters]
  )

  const setIndexRange = useCallback(
    (indexMin, indexMax) => {
      setFilters((s) => ({ ...s, indexMin, indexMax }))
    },
    [setFilters]
  )

  const setPartners = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partners: value }))
    },
    [setFilters]
  )

  const setPartnerGroup = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, partnersGroup: value }))
    },
    [setFilters]
  )

  const setDelta = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, delta: value }))
    },
    [setFilters]
  )

  const setDeltaTotal = useCallback(
    (value) => {
      setFilters((s) => ({ ...s, deltaTotal: value }))
    },
    [setFilters]
  )

  const filter = useCallback((items, filters) => {
    return items.filter((item) => {
      if (!firstBlockFilter(item, filters)) return false
      if (!secondBlockFilter(item, filters)) return false

      return true
    })
  }, [])

  return {
    filters,
    filtersData,
    initFilters,
    setAutomaticRange,
    setProposalRange,
    setCapRange,
    setIndexRange,
    setPartners,
    setPartnerGroup,
    setDelta,
    setDeltaTotal,
    filter,
    resetFilters,
    initialized: filters.initialized,
  }
}
export default useCapManagementFilters
